import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

type ConsentTermPreviewModalProps = {
  isOpen: boolean;
  title: string;
  body: string;
  onClose: () => void;
  /** When true, primary action stays disabled until the user scrolls to the end. */
  requireScrollToEnd?: boolean;
  onConfirmedRead?: () => void;
};

/**
 * Full-screen readable preview of legal consent text (tablet-friendly) before signing.
 */
const ConsentTermPreviewModal = ({
  isOpen,
  title,
  body,
  onClose,
  requireScrollToEnd = true,
  onConfirmedRead,
}: ConsentTermPreviewModalProps) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [reachedEnd, setReachedEnd] = useState(!requireScrollToEnd);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setReachedEnd(!requireScrollToEnd);
    window.requestAnimationFrame(() => {
      const node = scrollRef.current;
      if (!node || !requireScrollToEnd) {
        return;
      }
      if (node.scrollHeight <= node.clientHeight + 8) {
        setReachedEnd(true);
      }
    });
  }, [isOpen, body, requireScrollToEnd]);

  const handleScroll = () => {
    const node = scrollRef.current;
    if (!node || !requireScrollToEnd) {
      return;
    }
    const remaining = node.scrollHeight - node.scrollTop - node.clientHeight;
    if (remaining <= 24) {
      setReachedEnd(true);
    }
  };

  const handleConfirm = () => {
    onConfirmedRead?.();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      fullscreen
      centered
      contentClassName="d-flex flex-column"
    >
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody className="flex-grow-1 p-0 d-flex flex-column" style={{ minHeight: 0 }}>
        <div
          ref={scrollRef}
          className="flex-grow-1 overflow-auto p-3"
          onScroll={handleScroll}
        >
          <p
            className="mb-0"
            style={{ whiteSpace: "pre-wrap", fontSize: "1.05rem", lineHeight: 1.6 }}
          >
            {body || t("clients.anamnesis.fields.consentTextEmpty")}
          </p>
        </div>
      </ModalBody>
      <ModalFooter className="d-flex flex-column align-items-stretch gap-2">
        {requireScrollToEnd && !reachedEnd ? (
          <span className="text-muted small">
            {t("clients.anamnesis.preview.scrollHint")}
          </span>
        ) : null}
        <div className="d-flex justify-content-end gap-2">
          <Button color="light" type="button" onClick={onClose}>
            {t("clients.anamnesis.preview.dismiss")}
          </Button>
          <Button
            color="primary"
            type="button"
            disabled={requireScrollToEnd && !reachedEnd}
            onClick={handleConfirm}
          >
            {t("clients.anamnesis.preview.close")}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ConsentTermPreviewModal;
